import React, { memo, useContext, useMemo } from "react";
// context
import { CartContext } from "@cart-client/contexts/CartContext";
// types
import { MRO_CART_TESTIDS } from "@swiggy-private/cart-test-ids";
// helpers
import {
    getMROChildCartByRestaurantId,
    logMROEventsIntoNR,
    getMroMenuCalloutInfo,
} from "@cart-client/helpers/MROFoodCart";
import { getMenuCalloutInfo } from "@cart-client/helpers/Cart";
import { isEmpty } from "@swiggy-private/checkout-core";
import { hasSelectedAddress } from "@cart-client/helpers/CartFooter";
// interfaces
import { MROReviewedCart } from "@cart-client/interfaces/MROFoodCart";
// constants
import { MRO_DEFAULT_EMPTY_ARRAY_PLACEHOLDER } from "@cart-client/constants/MROConstants";
import {
    CART_STATUS_CODES,
    CartItemRendererProps,
    ItemImagesOnCartXPVariants,
    MROChildCart,
    ReviewedCart,
} from "@swiggy-private/cart-types";
// components
import { CartGroupContainer, format } from "@swiggy-private/core-ui";
import { MenuItemsCallout, MRORxCardHeader } from "@swiggy-private/cart-ui";
import { CartItemRenderer } from "@cart-client/components/cart-item-renderer";
import { ExtraItemList } from "@cart-client/components/extra-item-list";
import { CartItemActions } from "@cart-client/components/cart-items-actions";
import { OneMemberShipNudgeWrapper } from "@cart-client/components/one-membership-nudge-wrapper";
// styles
import { CartGroupContainerWrapper } from "../styles";
//hooks
import { useCartDeliverySpeed } from "@cart-client/hooks/useCartDeliverySpeed";

interface MenuItemWidgetProps {
    restaurantId?: number | string;
    menuItemsCalloutAnalytics: {
        onItemsBannerImpression: (message?: string | undefined) => void;
        onItemsBannerClick: (message?: string | undefined) => void;
        onHalfCardBannerImpression: () => void;
        onHalfCardBannerCtaClick: () => void;
    };
    isBulkOrderFn: boolean;
    isValueMeal: boolean;
    itemImageOnCartXpFlagExcludingBulkOrder: ItemImagesOnCartXPVariants;
    isIrctcCartSession: boolean;
    disableRedirectionToMenu: boolean;
    isCroutonVisible: boolean;
    goToMenu: (restaurantId?: string | number) => void;
    isPopCart: boolean;
    isMROCart?: boolean;
}

export const MenuItemWidget = ({
    restaurantId,
    menuItemsCalloutAnalytics,
    isBulkOrderFn,
    isValueMeal,
    itemImageOnCartXpFlagExcludingBulkOrder,
    isIrctcCartSession,
    disableRedirectionToMenu,
    isCroutonVisible,
    goToMenu,
    isPopCart,
    isMROCart = false,
}: MenuItemWidgetProps) => {
    const { cartState } = useContext(CartContext);
    const { reviewedCart: cartResponse, localCart } = cartState;

    const childCart = useMemo(() => {
        return !isEmpty(restaurantId)
            ? getMROChildCartByRestaurantId(cartResponse as MROReviewedCart, restaurantId)
            : undefined;
    }, [cartResponse, restaurantId]);

    const cart = useMemo(() => {
        return isMROCart && !isEmpty(childCart) ? childCart : cartResponse?.data;
    }, [childCart, cartResponse, isMROCart]);

    const {
        onDeliverySpeedItemClick,
        hideDeliverySpeedCrouton,
        selectedSlaOption,
        deliverySpeedCroutonText,
        deliverySpeedCroutonSubText,
        deliverySpeedCroutonIcon,
        deliverySpeedWidgetInfoV2,
        onCroutonClick,
        onCroutonLoad,
        onOptInWidgetClick,
        selectedAddress,
    } = useCartDeliverySpeed({
        cartResponse: cart as MROChildCart,
    });

    const itemListHeader = useMemo(() => {
        return cart?.coupon_details?.coupon_selective_discounting_card ?? cart?.offerPresentationInfo?.cartMessageCard;
    }, [cart?.coupon_details?.coupon_selective_discounting_card, cart?.offerPresentationInfo?.cartMessageCard]);

    const orderComment = useMemo(() => {
        const orderComment = isMROCart ? (cart as MROChildCart)?.orderComment : localCart?.orderComment;
        return orderComment ?? "";
    }, [cart, isMROCart, localCart?.orderComment]);

    const childCartProps = useMemo(() => {
        return {
            cartItems: cart?.cart_menu_items ?? [],
            groupTagDetails: cart?.group_tag_details,
            restaurantDetails: cart?.restaurant_details,
            couponDetails: cart?.coupon_details,
            cartMenuItemsHeaderList: cart?.cart_menu_items_header_list,
        };
    }, [cart]);

    const menuCallout = useMemo(() => {
        return isMROCart
            ? getMroMenuCalloutInfo(cart as MROChildCart)
            : getMenuCalloutInfo(cartResponse as ReviewedCart);
    }, [isMROCart, cart, cartResponse]);

    const dataTestId = useMemo(() => {
        return isMROCart && !isEmpty(restaurantId) ? format(MRO_CART_TESTIDS.CONTAINER, String(restaurantId)) : "";
    }, [isMROCart, restaurantId]);

    const cartResponseData = useMemo(() => {
        return isMROCart ? ({ data: cart } as MROReviewedCart) : cartResponse;
    }, [isMROCart, cart, cartResponse]);

    if (isMROCart && !isEmpty(cartResponse?.data) && isEmpty(childCart)) {
        logMROEventsIntoNR({
            type: "mro_cart_rendering_error_rxid_mismatch",
            restaurantId: String(restaurantId),
        });
        throw new Error("MRO cart rendering for a specific restaurantId failed");
    }

    if (isEmpty(cart)) {
        return null;
    }

    const hasMroCartError = isMROCart && (cart as MROChildCart)?.statusCode != CART_STATUS_CODES.SUCCESS;
    const isAddressSelected = hasSelectedAddress(cartResponse?.data?.addresses);
    return (
        <CartGroupContainerWrapper data-testid={dataTestId}>
            <CartGroupContainer hasErrorBorder={hasMroCartError} allowOverflow={isMROCart}>
                {isMROCart && deliverySpeedWidgetInfoV2 && (
                    <MRORxCardHeader
                        cart={cart as MROChildCart}
                        deliverySpeedWidgetInfoV2={deliverySpeedWidgetInfoV2}
                        onDeliverySpeedItemClick={onDeliverySpeedItemClick}
                        hideDeliverySpeedCrouton={hideDeliverySpeedCrouton}
                        selectedSlaOption={selectedSlaOption}
                        deliverySpeedCroutonText={deliverySpeedCroutonText}
                        deliverySpeedCroutonSubText={deliverySpeedCroutonSubText}
                        deliverySpeedCroutonIcon={deliverySpeedCroutonIcon}
                        onCroutonClick={onCroutonClick}
                        onCroutonLoad={onCroutonLoad}
                        onOptInWidgetClick={onOptInWidgetClick}
                        selectedAddress={selectedAddress}
                        calloutInfo={menuCallout}
                        hasMroCartError={hasMroCartError}
                        isAddressSelected={isAddressSelected}
                    />
                )}
                {!isMROCart && <OneMemberShipNudgeWrapper />}
                {!isMROCart && (
                    <MenuItemsCallout calloutInfo={menuCallout?.info} analyticsPayload={menuItemsCalloutAnalytics} />
                )}
                <CartItemRenderer
                    cartItems={childCartProps.cartItems}
                    groupTagDetails={childCartProps.groupTagDetails}
                    mealItems={MRO_DEFAULT_EMPTY_ARRAY_PLACEHOLDER as CartItemRendererProps["mealItems"]}
                    restaurantDetails={childCartProps.restaurantDetails}
                    couponDetails={childCartProps.couponDetails}
                    cartMenuItemsHeaderList={childCartProps.cartMenuItemsHeaderList}
                    itemListHeader={itemListHeader}
                    showInputQtyField={isBulkOrderFn}
                    isValueMeal={isValueMeal}
                    itemImagesXP={itemImageOnCartXpFlagExcludingBulkOrder}
                    isIrctcCartSession={isIrctcCartSession}
                    restaurantId={restaurantId}
                />
                {!isMROCart && <ExtraItemList cartResponse={cartResponse as ReviewedCart} />}
                <CartItemActions
                    disableRedirectionToMenu={disableRedirectionToMenu}
                    specialInstruction={orderComment}
                    isCroutonVisible={isCroutonVisible}
                    isBulkOrderFn={isBulkOrderFn}
                    cartResponse={cartResponseData}
                    goToMenu={goToMenu}
                    isPopCart={isPopCart}
                    isIrctcCartSession={isIrctcCartSession}
                    restaurantId={restaurantId}
                />
            </CartGroupContainer>
        </CartGroupContainerWrapper>
    );
};

export default memo(MenuItemWidget);
